import React, { useEffect, useState, useRef } from 'react';
import Footer from '../component/Footer/FooterCommon';
import Navbar from '../component/Navbar/NavbarCommon';
import ArrowButton from '../component/Common/ArrowButton';
import { api } from '../api';
import { API_URL, ASSETS_URL } from '../utils/config';
import { toast } from 'react-toastify';

interface Video {
  _id: string;
  title: string;
  description: string;
  videoUrl: string;
  bannerUrl: string;
  status: string;
  createdAt: string;
}

const VideosList = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState<Video | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  // Close modal on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        setActiveVideo(null);
      }
    };
    if (activeVideo) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeVideo]);

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveVideo(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const fetchVideos = async () => {
    try {
      const { data, error } = await api.get(`${API_URL.videos}?status=published&limit=50`);
      if (error) { toast.error(error.message); return; }
      setVideos((data as any)?.data?.videos || []);
    } catch {
      toast.error('Failed to fetch videos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-poppins relative overflow-hidden">
      <Navbar />

      {/* Blue gradient at bottom */}
      <div className="fixed bottom-0 left-0 right-0 h-40 pointer-events-none z-0">
        <div className="w-full h-full bg-gradient-to-t from-blue-100/60 via-blue-50/40 to-transparent" />
      </div>

      <div className="flex-grow py-4 lg:py-6 px-4 lg:px-10 mt-20 md:mt-2 lg:mt-28 xl:mt-28 font-poppins overflow-x-hidden">
        <ArrowButton />

        {/* Header */}
        <div className="max-w-full mx-auto mb-8 sm:mb-12 md:mb-16 lg:mb-8 xl:mb-10 lg:mt-5 xl:mt-10">
          <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-semibold mb-4">
            <span className="text-navgreen">Our </span>
            <span className="text-black">Videos</span>
          </h1>
          <p className="text-sm md:text-base lg:text-lg text-deepTeal font-medium max-w-2xl">
            Watch our latest videos, highlights, and company stories.
          </p>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-gray-500 text-lg">Loading videos...</div>
          </div>
        ) : videos.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-gray-500 text-lg">No videos available yet.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
            {videos.map((video) => (
              <div
                key={video._id}
                onClick={() => setActiveVideo(video)}
                className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden cursor-pointer group"
              >
                {/* Thumbnail */}
                <div className="relative w-full aspect-video bg-black overflow-hidden">
                  {video.bannerUrl ? (
                    <img
                      src={`${ASSETS_URL}${video.bannerUrl}`}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-navgreen/20 to-navgreen/50 flex items-center justify-center">
                      <svg className="w-16 h-16 text-navgreen/40" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  )}
                  {/* Play overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                      <svg className="w-7 h-7 text-navgreen ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="text-sm sm:text-base font-semibold text-deepTeal mb-1 line-clamp-2 group-hover:text-navgreen transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed line-clamp-2">
                    {video.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      {activeVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div
            ref={modalRef}
            className="bg-black rounded-2xl overflow-hidden w-full max-w-3xl shadow-2xl"
          >
            {/* Close button */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-900">
              <p className="text-white font-semibold text-sm sm:text-base line-clamp-1 flex-1 mr-4">
                {activeVideo.title}
              </p>
              <button
                onClick={() => setActiveVideo(null)}
                className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Player */}
            <div className="w-full aspect-video">
              <video
                key={activeVideo._id}
                controls
                autoPlay
                className="w-full h-full"
              >
                <source src={`${ASSETS_URL}${activeVideo.videoUrl}`} />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Description */}
            {activeVideo.description && (
              <div className="px-4 py-3 bg-gray-900">
                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed line-clamp-3">
                  {activeVideo.description}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default VideosList;
